import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { User } from '../lib/db/types';

const getAuthRedirectUrl = (path: string) => {
  const override = (import.meta as any)?.env?.VITE_PUBLIC_SITE_URL;
  const base = override ? String(override).replace(/\/$/, '') : window.location.origin;
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
};

async function postWebnotiEvent(accessToken: string, event: 'login' | 'register') {
  try {
    await fetch('/api/webnoti/event', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        access_token: accessToken,
        event,
        target: 'user',
      }),
    });
  } catch {
    // ignore
  }
}

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Cargar sesión primero (no bloqueante)
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
      
      // Verificar status en segundo plano (después de cargar)
      if (session?.user) {
        setTimeout(() => checkUserStatus(session.user.id), 100);
      }
    });

    // Escuchar cambios de autenticación
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
      
      // Verificar status en segundo plano
      if (session?.user) {
        setTimeout(() => checkUserStatus(session.user.id), 100);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Función para verificar status (ejecutada en segundo plano)
  const checkUserStatus = async (userId: string) => {
    try {
      const { data: userData } = await supabase
        .from('users')
        .select('status')
        .eq('id', userId)
        .maybeSingle();

      const status = userData?.status || 'active';

      if (status === 'inactive') {
        await supabase.auth.signOut();
        setUser(null);
        alert('Tu cuenta ha sido desactivada. Contacta al administrador.');
      }
    } catch (error) {
      // No bloquear si hay error (tabla no existe, RLS, etc)
      console.warn('No se pudo verificar status:', error);
    }
  };

  const signUp = async (
    email: string,
    password: string,
    fullName: string,
    accountType: 'business' | 'accountant' = 'business',
  ) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            account_type: accountType,
          },
          emailRedirectTo: getAuthRedirectUrl('/auth/reset-password'),
        },
      });

      if (error) throw error;

       const accessToken = data?.session?.access_token;
       if (accessToken) {
         await postWebnotiEvent(accessToken, 'register');
       }

      return { data, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

       const accessToken = data?.session?.access_token;
       if (accessToken) {
         await postWebnotiEvent(accessToken, 'login');
       }

      return { data, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return { error: null };
    } catch (error: any) {
      return { error: error.message };
    }
  };

  const verifyEmailExists = async (email: string) => {
    try {
      const trimmed = String(email || '').trim();
      if (!trimmed) return { exists: false, error: 'Por favor ingresa tu correo electrónico' };
      if (!/\S+@\S+\.\S+/.test(trimmed)) return { exists: false, error: 'Por favor ingresa un email válido' };

      const { data, error } = await supabase.rpc('email_exists', {
        email_to_check: trimmed,
      });

      if (error) throw error;
      return { exists: Boolean(data), error: null };
    } catch (error: any) {
      return { exists: false, error: error.message };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const resetRedirectOverride = (import.meta as any)?.env?.VITE_PASSWORD_RESET_REDIRECT_URL;
      const redirectTo = resetRedirectOverride
        ? String(resetRedirectOverride)
        : getAuthRedirectUrl('/auth/callback');

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo,
      });

      if (error) throw error;

      return { error: null };
    } catch (error: any) {
      return { error: error.message };
    }
  };

  const updatePassword = async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      return { error: null };
    } catch (error: any) {
      return { error: error.message };
    }
  };

  return {
    user,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    verifyEmailExists,
    updatePassword,
  };
};
